import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import {
  SubnavDesign,
  SubnavOption,
} from 'src/components/Subnav/Subnav'
import { OrganizationRoles } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { useVirtualCardsFilters } from 'src/hooks/virtualCardHooks'
import Trns from 'src/localization/Trns'
import VirtualCardsAppRoute from 'src/navigation/appRoutes/virtualCards.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  actionUnavailableUrl,
  createVirtualCardUrl,
  myWalletVirtualCardsUrl,
  requestVirtualCardUrl,
  virtualCardsAllCardsUrl,
  virtualCardsNeedsApprovalUrl,
} from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { selectStatisticsDispatcherFromState } from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsUserOrViewerMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectVirtualCardsAllCardsMatch,
  selectVirtualCardsNeedsApprovalMatch,
  selectVirtualCardsRequestsMatch,
  selectVirtualCardsYourCardsMatch,
} from 'src/redux/selectors/router.selectors'

const VirtualCards: React.FC = () => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const virtualCardsAllCardsMatch = useSelector(selectVirtualCardsAllCardsMatch)
  const virtualCardsYourCardsMatch = useSelector(
    selectVirtualCardsYourCardsMatch,
  )
  const virtualCardsRequestsMatch = useSelector(selectVirtualCardsRequestsMatch)
  const virtualCardsNeedsApprovalMatch = useSelector(
    selectVirtualCardsNeedsApprovalMatch,
  )
  const creditCardsCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsUserOrViewerMeCount = useSelector(
    selectCreditCardsUserOrViewerMeCountStatisticsFromState,
  )
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestRole =
    user?.organizationRole === OrganizationRoles.guest && showGuestRole
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
    ])
  }, [dispatchStatistics])

  const filtersBody = useVirtualCardsFilters()

  const { data: directReports } = useQueryPeopleManagerDirectReports({
      organizationId: user?.organizationId,
      userId: user?.id,
    })
  const isManager = directReports !== undefined && directReports.length > 0

  const hasCreditCards = creditCardsUserOrViewerMeCount > 0
  const canIssueVirtualCard = creditCardsCanIssueVirtualCardsCount > 0
  const canCurrentlyIssueVirtualCard =
    creditCardsActiveCanIssueVirtualCardsCount > 0
  const hasAccessToAllCards =
    (hasCreditCards || user?.hasOrgBookkeeperPermissions || isManager) && !isGuestRole

  const subNavOptions: SubnavOption[] = useMemo(() => {
    let optionsArr: SubnavOption[] = []
    if (virtualCardsYourCardsMatch) {
      optionsArr = [
        {
          label: <Trns source={'Virtual Cards'} />,
          to: myWalletVirtualCardsUrl(),
          isActive: virtualCardsYourCardsMatch !== null,
          ariaLabelSource: 'Virtual Cards',
          'data-testid': 'MyWalletVirtualCards',
        },
      ]
    } else if (
      virtualCardsAllCardsMatch ||
      virtualCardsRequestsMatch ||
      virtualCardsNeedsApprovalMatch
    ) {
      optionsArr = [
        {
          label: <Trns source={'All Cards'} />,
          to: virtualCardsAllCardsUrl(),
          isActive: virtualCardsAllCardsMatch !== null,
          ariaLabelSource: 'All Cards',
          'data-testid': 'AllCardsVirtualCards',
        },
        {
          label: <Trns source={'Needs Approval'} />,
          to: virtualCardsNeedsApprovalUrl(),
          isActive:
            virtualCardsRequestsMatch !== null ||
            virtualCardsNeedsApprovalMatch !== null,
          ariaLabelSource: 'Needs Approval',
          'data-testid': 'RequestsNeedsApproval',
        },
      ]
    }
    return optionsArr
  }, [
    virtualCardsAllCardsMatch,
    virtualCardsRequestsMatch,
    virtualCardsNeedsApprovalMatch,
    virtualCardsYourCardsMatch,
  ])

  const buttons = useMemo(
    () =>
      canIssueVirtualCard
        ? virtualCardsAllCardsMatch
          ? [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: <Trns source={'Download All Virtual Cards (CSV)'} />,
                onClick: (): void => {
                  analytics?.track(
                    'virtual cards - all cards - download all virtual cards',
                  )
                  dispatch(postReportVirtualCardsDownload(filtersBody))
                },
              },
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: <Trns source={'Request Card'} />,
                linkProps: {
                  to: requestVirtualCardUrl(),
                  ariaLabel: 'Request Card',
                },
              },
              {
                design: ButtonDesigns.designCta,
                type: ButtonTypes.button,
                text: <Trns source={'Create Card'} />,
                linkProps: {
                  to: canCurrentlyIssueVirtualCard
                    ? createVirtualCardUrl()
                    : actionUnavailableUrl(),
                  ariaLabel: 'Create Card',
                },
              },
            ]
          : [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: <Trns source={'Request Card'} />,
                linkProps: {
                  to: requestVirtualCardUrl(),
                  ariaLabel: 'Request Card',
                },
              },
              {
                design: ButtonDesigns.designCta,
                type: ButtonTypes.button,
                text: <Trns source={'Create Card'} />,
                linkProps: {
                  to: canCurrentlyIssueVirtualCard
                    ? createVirtualCardUrl()
                    : actionUnavailableUrl(),
                  ariaLabel: 'Create Card',
                },
              },
            ]
        : isGuestRole
          ? [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: <Trns source={'Download All Virtual Cards (CSV)'} />,
                onClick: (): void => {
                  analytics?.track(
                    'virtual cards - all cards - download all virtual cards',
                  )
                  dispatch(postReportVirtualCardsDownload(filtersBody))
                },
              },
            ]
          : [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: <Trns source={'Download All Virtual Cards (CSV)'} />,
                onClick: (): void => {
                  analytics?.track(
                    'virtual cards - all cards - download all virtual cards',
                  )
                  dispatch(postReportVirtualCardsDownload(filtersBody))
                },
              },
              {
                design: ButtonDesigns.designCta,
                type: ButtonTypes.button,
                text: <Trns source={'Request Card'} />,
                linkProps: {
                  to: requestVirtualCardUrl(),
                  ariaLabel: 'Request Card',
                },
              },
            ],
    [
      canIssueVirtualCard,
      canCurrentlyIssueVirtualCard,
      isGuestRole,
      filtersBody,
      virtualCardsAllCardsMatch,
    ],
  )

  return (
    <LayoutShell
      subnavOptions={subNavOptions}
      subnavDesign={SubnavDesign.Floating}
      content={
        <Switch location={calculatedBackgroundLocation}>
          {VirtualCardsAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ))}
          <Route {...VirtualCardsAppRoute.routeProps} exact>
            <Rdrct
              to={
                hasAccessToAllCards
                  ? virtualCardsAllCardsUrl()
                  : myWalletVirtualCardsUrl()
              }
            />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
      title={
        <Trns
          source={virtualCardsYourCardsMatch ? 'My Wallet' : 'Virtual Cards'}
        />
      }
      buttons={buttons}
    />
  )
}

export default VirtualCards
